import React from 'react';
import uuid from 'uuid';
import { formatWholeNumber } from '../../../../../../core/utils/campaigns';
import DataTable from '../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// SegmentsTable — Audience targeting breakdown, now on DataTable.
//
// Parity-preserving logic from v1:
//   - Merge rule: rows where audience_id is 'Open Targeting' or 'N/A' get
//     collapsed into a single combined bucket (sum of clicks + ais). This
//     prevents the audience list from being swamped by repeated generic rows.
//   - Suppress zero-activity Open Targeting after merging — if the merged
//     bucket has no impressions and no clicks it's noise.
//   - Stable-last sort: "Open Targeting" always sorts to the bottom regardless
//     of column direction.
//
// CTR is computed here (v1 did the same inline) since segment rows don't
// always carry a pre-computed CTR.
// =============================================================================

const OPEN_TARGETING_LABELS = new Set(['Open Targeting', 'N/A']);

const isOpenTargeting = (row) =>
  OPEN_TARGETING_LABELS.has(row.audience_id) ||
  row.retargeting_name === 'Open Targeting';

// Keep Open Targeting pinned to the bottom.
const stableSort = (columnSort) => (rowA, rowB, columnId) => {
  const a = rowA.original;
  const b = rowB.original;
  const aOpen = isOpenTargeting(a);
  const bOpen = isOpenTargeting(b);
  if (aOpen && !bOpen) return 1;
  if (!aOpen && bOpen) return -1;
  if (aOpen && bOpen) return 0;
  return columnSort(rowA, rowB, columnId);
};

const numericSort = (key) => (rowA, rowB) => {
  return (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);
};

const nameSort = (rowA, rowB) => {
  const a = rowA.original.retargeting_name || rowA.original.audience_id || '';
  const b = rowB.original.retargeting_name || rowB.original.audience_id || '';
  return String(a).localeCompare(String(b));
};

const SegmentsTable = ({ segmentsData }) => {
  // v1 merge/filter logic, preserved verbatim so no behavior drift.
  const openTargetingSegments = [];
  const otherSegments = [];
  segmentsData.forEach((segment) => {
    if (segment.audience_id === 'Open Targeting' || segment.audience_id === 'N/A') {
      openTargetingSegments.push(segment);
    } else {
      otherSegments.push(segment);
    }
  });
  const mergedSegmentsData = [];
  if (openTargetingSegments.length > 1) {
    const mergedOpenTargeting = openTargetingSegments.slice(1).reduce(
      (acc, curr) => {
        for (const key of ['clicks', 'ais']) {
          acc[key] += curr[key];
        }
        return acc;
      },
      { ...openTargetingSegments[0] }
    );
    mergedSegmentsData.push(...otherSegments, mergedOpenTargeting);
  } else if (openTargetingSegments.length === 1) {
    mergedSegmentsData.push(...otherSegments, openTargetingSegments[0]);
  } else {
    mergedSegmentsData.push(...segmentsData);
  }

  const rows = mergedSegmentsData
    .map((segments) => ({
      ...segments,
      key: `${segments.id}${uuid.v4()}`,
      retargeting_id: segments.retargeting_id,
      audience_id:
        segments.audience_id === null ? segments.retargeting_name : segments.audience_id,
      ctr: segments.ais ? (segments.clicks / segments.ais) * 100 : 0,
    }))
    .filter((segment) => {
      // Hide zero-activity Open Targeting rows — they add no info.
      if (
        (segment.audience_id === 'Open Targeting' || segment.audience_id === 'N/A') &&
        segment.ais === 0 &&
        segment.clicks === 0
      ) {
        return false;
      }
      return true;
    });

  const columns = [
    {
      id: 'retargeting_name',
      accessorFn: (row) => row.retargeting_name || row.audience_id,
      header: 'Audience Targeting',
      sortable: true,
      sortingFn: stableSort(nameSort),
      meta: { align: 'left', width: 280 },
      cell: ({ getValue }) => (
        <span className="campaign-overview__creatives-name">
          {getValue() || 'Audience Name Not Available'}
        </span>
      ),
    },
    {
      id: 'ais',
      accessorKey: 'ais',
      header: 'Impressions',
      sortable: true,
      sortingFn: stableSort(numericSort('ais')),
      meta: { align: 'right', width: 140 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? formatWholeNumber(v) : '-';
      },
    },
    {
      id: 'clicks',
      accessorKey: 'clicks',
      header: 'Clicks',
      sortable: true,
      sortingFn: stableSort(numericSort('clicks')),
      meta: { align: 'right', width: 110 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? formatWholeNumber(v) : '-';
      },
    },
    {
      id: 'ctr',
      accessorKey: 'ctr',
      header: 'CTR',
      sortable: true,
      sortingFn: stableSort(numericSort('ctr')),
      meta: { align: 'right', width: 100 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? `${v.toFixed(3)}%` : '-';
      },
    },
  ];

  const totalAis = rows.reduce((sum, d) => sum + (parseFloat(d.ais) || 0), 0);
  const totalClicks = rows.reduce((sum, d) => sum + (parseFloat(d.clicks) || 0), 0);

  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span>{record.retargeting_name || record.audience_id || 'Audience Name Not Available'}</span>
        </div>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.ais ? formatWholeNumber(record.ais) : '-'}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Clicks</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.clicks ? formatWholeNumber(record.clicks) : '-'}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">CTR</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.ctr ? `${record.ctr.toFixed(3)}%` : '-'}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      summaryRow={{
        labelColumnId: 'retargeting_name',
        label: 'Totals + Averages',
        cells: {
          ais: formatWholeNumber(totalAis),
          clicks: formatWholeNumber(totalClicks),
          ctr: '-',
        },
      }}
      emptyState="No audience targeting data yet."
    />
  );
};

export default SegmentsTable;
